import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, Star, MapPin, Phone, Mail, Shield, Search, ArrowLeft, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function CarrierDirectory() {
  const { user } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCarriers();
  }, []);

  const loadCarriers = async () => {
    try {
      const res = await apiClient.get('/carriers');
      setCarriers(res.data);
    } catch (error) {
      console.error('Failed to load carriers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCarriers = carriers.filter(carrier => {
    const searchLower = searchTerm.toLowerCase();
    return carrier.company_name?.toLowerCase().includes(searchLower) ||
           carrier.first_name.toLowerCase().includes(searchLower) ||
           carrier.last_name.toLowerCase().includes(searchLower);
  });

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < Math.round(rating) ? 'star-filled fill-current' : 'star-empty'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="btn-accent" data-testid="dashboard-link">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="btn-accent" data-testid="login-link">Get Started</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-3xl font-bold">Verified Carriers</h1>
          <p className="text-slate-300 mt-2">Browse and connect with verified trucking companies directly</p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search carriers by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Carrier Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredCarriers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Building className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No carriers found</h3>
            <p className="text-slate-500">Try adjusting your search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCarriers.map((carrier) => (
              <div key={carrier.id} className="card-marketing" data-testid={`carrier-card-${carrier.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Truck className="h-6 w-6 text-slate-600" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Shield className="h-4 w-4 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">Verified</span>
                  </div>
                </div>
                
                <h3 className="font-bold text-slate-900 text-lg">
                  {carrier.company_name || `${carrier.first_name} ${carrier.last_name}`}
                </h3>
                
                {carrier.company_name && (
                  <p className="text-slate-500 text-sm">{carrier.first_name} {carrier.last_name}</p>
                )}
                
                <div className="flex items-center gap-2 mt-3">
                  {renderStars(carrier.rating)}
                  <span className="text-sm text-slate-500">
                    {carrier.rating > 0 ? `${carrier.rating.toFixed(1)}` : 'No reviews'}
                  </span>
                </div>
                
                <div className="mt-4 space-y-2 text-sm text-slate-500">
                  {carrier.dot_number && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">DOT:</span>
                      <span className="mono">{carrier.dot_number}</span>
                    </div>
                  )}
                  {carrier.mc_number && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">MC:</span>
                      <span className="mono">{carrier.mc_number}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Jobs Completed:</span>
                    <span>{carrier.total_jobs}</span>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-100">
                  {user ? (
                    <Link to="/customer/dashboard">
                      <Button className="w-full btn-primary" data-testid={`contact-carrier-${carrier.id}`}>
                        Request Quote
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/auth?role=customer">
                      <Button className="w-full btn-secondary">
                        Sign in to Contact
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
